// SimplifiedTournamentCanvas.jsx - Componente principale del canvas
import React, { useState, useRef, useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import Node from './Node.jsx';
import Connection from './Connection.jsx';
import PropertyEditor from './PropertyEditor.jsx';
import CompoundNode from './CompoundNode.jsx';
import { useDragAndDrop } from './useDragAndDrop.jsx';
import { AlignmentToolbar } from './AlignmentToolbar.jsx';
import './TournamentFormulaCanvas.css';
import API from '../../api/API.jsx';
import { zoomAncorato, interpretaRotella, rettangoloFra, nodiNelRettangolo } from './canvasGestures';
import { COLORI_FRECCIA, idFreccia } from './frecceConnessioni';

const TournamentFormulaCanvas = ({ readOnly = false }) => {
  const [nodes, setNodes] = useState([]);
  const [connections, setConnections] = useState([]);
  const [selectedNodes, setSelectedNodes] = useState([]);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [connecting, setConnecting] = useState(null);
  const [hoveredNode, setHoveredNode] = useState(null);
  const [propertyEditorOpen, setPropertyEditorOpen] = useState(false);
  const [linkingMode, setLinkingMode] = useState(false);
  const [planningModalOpen, setPlanningModalOpen] = useState(false);
  const [sortPriorityModalOpen, setSortPriorityModalOpen] = useState(false);
  // Priorità globale per le classifiche dei gironi (null = default)
  const [formulaSortPriority, setFormulaSortPriority] = useState(null);
  // Gruppi di nodi dichiarati a mano come una sola fase: [{ id, nome, nodi: [originalId] }].
  // Servono dove il calcolo automatico sbaglia - per esempio spareggi che giocano solo alcune
  // squadre, in parallelo alla fase successiva e su campi diversi. Vuoto = calcolo automatico.
  const [gruppiFase, setGruppiFase] = useState([]);

  // Cache per le squadre
  const [cachedTeams, setCachedTeams] = useState(null);

  // Transform per zoom e pan
  const [transform, setTransform] = useState({
    scale: 1,
    translateX: 0,
    translateY: 0
  });

  // Il pan non sta nello stato di React.
  //
  // Ci stava, e bastava a rovinare tutto: setPanState al premere e al rilasciare faceva un commit
  // che cambiava la classe sull'<svg>, e Chrome da li' ridipingeva l'intero sottoalbero di quasi
  // 4000 elementi. Misurato sulla macchina di chi usa l'applicazione: un frame da 284ms a inizio
  // gesto, con tutto il resto del pan a 7ms. E' il blocco che si vedeva come movimento a elastico.
  // Il cursore si scrive a mano, che e' una proprieta' di solo disegno e costa niente.

  // Stato per selezione rettangolare
  const [selectionState, setSelectionState] = useState({
    isSelecting: false,
    startPoint: null,
    rect: null
  });
  
  const canvasRef = useRef(null);
  // Il <g> che porta translate/scale: durante un gesto glielo scriviamo addosso a mano.
  const contenutoRef = useRef(null);

  // Un gesto in corso (pan, zoom o trascinamento): mentre e' vero, la verita' sulla posizione
  // della vista sta in transformRef e non nello stato.
  const gestoInCorso = useRef(false);

  // Specchi in ref di stato che serve dentro handler chiamati a ogni pixel di movimento. Leggerlo
  // dalla ref invece che dalla closure tiene stabili le callback: altrimenti cambiano identita' a
  // ogni render e i listener su document vengono tolti e rimessi in continuazione.
  //
  // L'allineamento va fatto dopo il commit, non durante il render: React puo' renderizzare e poi
  // buttare via il risultato, e scrivendo la ref durante il render questa resterebbe avanti
  // rispetto a quello che sta davvero sullo schermo.
  const transformRef = useRef(transform);
  const nodesRef = useRef(nodes);
  const connectionsRef = useRef(connections);
  // Durante un gesto lo stato e' fermo all'inizio del gesto: riallineare la ref adesso
  // riporterebbe indietro la vista di scatto a ogni ridisegno, per esempio quando il puntatore
  // sfiora un nodo e cambia hoveredNode. E' il movimento a elastico.
  useLayoutEffect(() => {
    if (!gestoInCorso.current) transformRef.current = transform;
  }, [transform]);
  useEffect(() => { nodesRef.current = nodes; }, [nodes]);
  useEffect(() => { connectionsRef.current = connections; }, [connections]);
  const leggiNodi = useCallback(() => nodesRef.current, []);

  // Un gesto non passa dallo stato di React: scrive direttamente sull'attributo del DOM e allo
  // stato ci arriva solo alla fine. Alla taglia della formula del torneo 98 un frame che passa da
  // setState fa ricreare e riconciliare 300 elementi anche senza che un figlio cambi.

  // --- Strumentazione (?debug=1) -----------------------------------------------------------
  // Misura nel browser vero quello che jsdom non puo' vedere: ogni quanto un frame arriva
  // davvero a schermo. Se l'intervallo fra due scritture sta intorno ai 16ms il browser tiene il
  // passo e il ritardo che si vede viene da altro (una transizione CSS, per esempio); se sta sui
  // 50-100ms il collo di bottiglia e' il ridisegno dell'SVG.
  const debug = useRef(typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('debug') === '1');
  const spia = useRef({ eventi: 0, frame: 0, ultimo: 0, intervalloMax: 0, intervalloUltimo: 0, commit: 0,
                        istanteEvento: 0, latUltima: 0, latMax: 0, latSomma: 0, latN: 0 });
  const spiaDomRef = useRef(null);

  // La latenza e' l'unico numero che descrive "l'immagine insegue il puntatore": quanto tempo
  // passa fra l'evento del mouse e la scrittura che lo porta a schermo. Gli intervalli fra frame
  // misurano il throughput, che puo' essere ottimo mentre la latenza e' pessima.
  const segnaEvento = useCallback((ev) => {
    if (!debug.current) return;
    spia.current.eventi++;
    spia.current.istanteEvento = ev?.timeStamp ?? performance.now();
  }, []);
  const segnaFrame = useCallback(() => {
    if (!debug.current) return;
    const s = spia.current;
    const ora = performance.now();
    if (s.ultimo) {
      s.intervalloUltimo = ora - s.ultimo;
      if (s.intervalloUltimo > s.intervalloMax) s.intervalloMax = s.intervalloUltimo;
    }
    s.ultimo = ora;
    s.frame++;
    if (s.istanteEvento) {
      const lat = ora - s.istanteEvento;
      s.latUltima = lat;
      if (lat > s.latMax) s.latMax = lat;
      s.latSomma += lat;
      s.latN++;
    }
    if (spiaDomRef.current) {
      spiaDomRef.current.textContent =
        `frame: ultimo ${s.intervalloUltimo.toFixed(0)}ms  peggiore ${s.intervalloMax.toFixed(0)}ms` +
        `  |  LATENZA evento->disegno: media ${(s.latN ? s.latSomma / s.latN : 0).toFixed(0)}ms` +
        `  peggiore ${s.latMax.toFixed(0)}ms  |  eventi ${s.eventi}  commit React ${s.commit}`;
    }
  }, []);
  const azzeraSpia = useCallback(() => {
    spia.current = { eventi: 0, frame: 0, ultimo: 0, intervalloMax: 0, intervalloUltimo: 0, commit: 0,
                     istanteEvento: 0, latUltima: 0, latMax: 0, latSomma: 0, latN: 0 };
    peggiorFrame.current = null;
    if (spiaFrameRef.current) spiaFrameRef.current.textContent = '';
  }, []);

  // Chi causa i frame lunghi, detto da Chrome invece che indovinato.
  //
  // L'API long-animation-frame scompone un frame lento in: quanto e' durato, quanto ha bloccato,
  // quanto se n'e' andato in stile e layout, e quali script l'hanno tenuto occupato, con nome di
  // funzione e file. E' l'unico modo per attribuire uno stallo su una macchina che non e' la mia.
  const peggiorFrame = useRef(null);
  const spiaFrameRef = useRef(null);

  useEffect(() => {
    if (!debug.current || typeof PerformanceObserver === 'undefined') return;
    if (!PerformanceObserver.supportedEntryTypes?.includes('long-animation-frame')) {
      if (spiaFrameRef.current) spiaFrameRef.current.textContent = 'long-animation-frame non supportato da questo browser';
      return;
    }

    const obs = new PerformanceObserver((lista) => {
      for (const e of lista.getEntries()) {
        if (peggiorFrame.current && e.duration <= peggiorFrame.current.duration) continue;
        peggiorFrame.current = e;
        const script = [...(e.scripts || [])].sort((a, b) => b.duration - a.duration)[0];
        const righe = [
          `FRAME LUNGO ${e.duration.toFixed(0)}ms  (blocco ${e.blockingDuration?.toFixed(0) ?? '?'}ms)`,
          `  stile+layout ${e.styleAndLayoutStart ? (e.startTime + e.duration - e.styleAndLayoutStart).toFixed(0) : '?'}ms` +
          `   render ${e.renderStart ? (e.startTime + e.duration - e.renderStart).toFixed(0) : '?'}ms`,
          script
            ? `  script ${script.duration.toFixed(0)}ms: ${script.invokerType || ''} ${script.sourceFunctionName || script.invoker || script.name || '?'}`
            : "  nessuno script: il tempo se ne va tutto nel browser",
          script?.sourceURL ? `  da ${String(script.sourceURL).replace(/^https?:\/\/[^/]+/, '')}` : '',
        ].filter(Boolean);
        if (spiaFrameRef.current) spiaFrameRef.current.textContent = righe.join(String.fromCharCode(10));
      }
    });
    obs.observe({ type: 'long-animation-frame', buffered: true });
    return () => obs.disconnect();
  }, []);

  useLayoutEffect(() => {
    if (debug.current && gestoInCorso.current) spia.current.commit++;
  });

  // Durante un gesto il contenuto del canvas si toglie dalla portata del puntatore e si dichiara
  // in movimento.
  //
  // Misurato con una traccia di Chrome su un pan della formula del 98: il hit testing costa ~230ms
  // di gesto, perche' a ogni movimento il browser cerca quale dei 3996 elementi SVG sta sotto il
  // puntatore - lavoro completamente inutile mentre trascini lo sfondo. E la ricostruzione
  // dell'artefatto di disegno per il compositore ne costa altri ~202ms: will-change dice a Chrome
  // di tenere il contenuto su un livello suo, cosi' spostarlo non lo obbliga a rifare il lavoro.
  //
  // Sono due proprieta' che si accendono solo per la durata del gesto: will-change lasciato acceso
  // sempre costa memoria video per niente.
  const modoGesto = useCallback((attivo) => {
    const g = contenutoRef.current;
    if (!g) return;
    g.style.pointerEvents = attivo ? 'none' : '';
    g.style.willChange = attivo ? 'transform' : '';
  }, []);

  // Si scrive la proprieta' CSS transform, non l'attributo SVG omonimo.
  //
  // Sono equivalenti a vedersi ma non a costarsi: l'attributo appartiene al layout dell'SVG, e
  // cambiarlo invalida il layout del sottoalbero: Blink deve rifare style, layout, prepaint e
  // paint prima di poter mostrare il frame. La proprieta' CSS passa dall'albero delle proprieta'
  // di disegno e il layout resta valido, quindi il percorso dall'evento al pixel e' piu' corto.
  //
  // transform-box: view-box e transform-origin: 0 0 servono a far coincidere le due semantiche:
  // senza, l'origine sarebbe il centro del riquadro del gruppo invece dell'origine del disegno.
  const scriviTransformSulDom = useCallback((t) => {
    const g = contenutoRef.current;
    if (!g) return;
    g.style.transform = `translate(${t.translateX}px, ${t.translateY}px) scale(${t.scale})`;
  }, []);

  // Unico punto in cui l'attributo arriva sul DOM fuori dai gesti. Gira dopo ogni commit, quindi
  // copre anche il primo montaggio e i cambi di transform che non vengono da un gesto (fitToView,
  // i pulsanti dello zoom).
  useLayoutEffect(() => {
    scriviTransformSulDom(transformRef.current);
  });

  const updateNode = useCallback((nodeId, updates) => {
    setNodes(prev => prev.map(node =>
      node.id === nodeId ? { ...node, ...updates } : node
    ));
  }, []);

  // Una sola passata per tutti i nodi mossi: trascinando una selezione di venti nodi, una
  // updateNode a testa significava venti scansioni dell'array per ogni pixel.
  const updateNodes = useCallback((aggiornamenti) => {
    const perId = new Map(aggiornamenti.map(a => [a.id, a]));
    setNodes(prev => prev.map(node => {
      const a = perId.get(node.id);
      return a ? { ...node, x: a.x, y: a.y } : node;
    }));
  }, []);

  // Hook per drag and drop
  const { isDragging, startDrag, updateDrag, endDrag } = useDragAndDrop(leggiNodi, updateNodes);

  // Dopo un pan il browser manda comunque un click sullo sfondo, che azzererebbe la selezione:
  // pannare per raggiungere un nodo lontano faceva perdere quello che avevi selezionato.
  const appenaPannato = useRef(false);

  // Funzioni di conversione formato dati
  const convertFromNewFormat = useCallback((data) => {
    const convertedNodes = data.fasi.map((fase, index) => {
      const baseNode = {
        id: `node-${fase.id}`,
        originalId: fase.id,
        name: fase.name,
        x: fase.XPos !== undefined ? fase.XPos : (fase.tipo === 'Squadra' ? 50 : 300 + (index * 50)),
        y: fase.YPos !== undefined ? fase.YPos : (fase.tipo === 'Squadra' ? 50 + (index * 45) : 200 + (index * 100)),
        width: 150,
        height: fase.tipo === 'Squadra' ? 40 : 80,
        incomingConnectionsOrder: {}
      };

      if (fase.tipo === 'Squadra') {
        return { ...baseNode, type: 'team', idSquadra: fase.idSquadra || null };
      } else if (fase.tipo === 'Dirette') {
        // Nodo compound per eliminazione diretta
        return {
          ...baseNode,
          type: 'phase',
          phaseType: fase.tipo,
          properties: { tipo: fase.tipo, partite: [] },
          // Struttura interna del tabellone
          expanded: fase.expanded ?? true,
          fascia: fase.fascia || 1,
          internalMatches: fase.internalMatches || [],
          internalConnections: fase.internalConnections || [],
          inputMapping: fase.inputMapping || [],
          outputMapping: fase.outputMapping || [],
          width: fase.width || 300,
          height: fase.height || 200
        };
      } else {
        const extra = {};
        if (fase.tipo === 'Classifica') {
          extra.sortPriority = Array.isArray(fase.sortPriority) && fase.sortPriority.length > 0
            ? fase.sortPriority
            : null;
        }
        return {
          ...baseNode,
          type: 'phase',
          phaseType: fase.tipo,
          properties: { tipo: fase.tipo, partite: [] },
          ...extra
        };
      }
    });

    const convertedConnections = data.links.map(link => ({
      id: `conn-${link.id}`,
      originalId: link.id,
      fromId: `node-${link.fromId}`,
      toId: `node-${link.toId}`,
      type: link.type,
      fromPosition: link.fromPosition,
      toPosition: link.toPosition,
      // Converte fromPosition in connectionNumber per compatibilità interna
      connectionNumber: (link.type === 'GironeToGirone' || link.type === 'GironeToDiretta' || 
                        link.type === 'GironeToClassifica' || link.type === 'DiretteToClassifica' || 
                        link.type === 'SquadraToClassifica' || link.type === 'ClassificaToGirone' ||
                        link.type === 'ClassificaToDiretta' || link.type === 'ClassificaToClassifica') 
        ? (link.fromPosition || 1) 
        : 0
    }));

    // Ricostruisce l'ordine delle connessioni in entrata basandosi su toPosition
    convertedNodes.forEach(node => {
      const incomingConnections = convertedConnections.filter(conn => conn.toId === node.id);
      if (incomingConnections.length > 0) {
        const orderMap = {};
        incomingConnections.forEach(conn => {
          if (conn.toPosition !== undefined && conn.toPosition !== null) {
            orderMap[conn.id] = conn.toPosition;
          }
        });
        
        // Se non ci sono posizioni definite, usa l'ordine di arrivo
        if (Object.keys(orderMap).length === 0) {
          incomingConnections.forEach((conn, index) => {
            orderMap[conn.id] = index;
          });
        }
        
        node.incomingConnectionsOrder = orderMap;
      }
    });

    return { nodes: convertedNodes, connections: convertedConnections };
  }, []);


  const convertToNewFormat = useCallback(() => {
    const fasi = nodes.map(node => {
      const baseData = {
        id: node.originalId || parseInt(node.id.replace('node-', '')),
        name: node.name,
        tipo: node.type === 'team' ? 'Squadra' : node.phaseType,
        XPos: node.x,
        YPos: node.y
      };

      if (node.type === 'team') {
        return { ...baseData, idSquadra: node.idSquadra };
      } else if (node.phaseType === 'Classifica') {
        return node.sortPriority && node.sortPriority.length > 0
          ? { ...baseData, sortPriority: node.sortPriority }
          : baseData;
      } else if (node.phaseType === 'Dirette') {
        // Serializza struttura interna del compound node
        return {
          ...baseData,
          expanded: node.expanded,
          width: node.width,
          height: node.height,
          fascia: node.fascia || 1,
          internalMatches: node.internalMatches || [],
          internalConnections: node.internalConnections || [],
          inputMapping: node.inputMapping || [],
          outputMapping: node.outputMapping || []
        };
      } else {
        return baseData;
      }
    });

    const links = connections.map(conn => {
      const fromNode = nodes.find(n => n.id === conn.fromId);
      const targetNode = nodes.find(n => n.id === conn.toId);
      
      const baseLink = {
        id: conn.originalId || parseInt(conn.id.replace('conn-', '')),
        fromId: parseInt(conn.fromId.replace('node-', '')),
        toId: parseInt(conn.toId.replace('node-', '')),
        type: conn.type || 'SquadraToGirone'
      };

      // Imposta fromPosition in base al tipo di link
      if (conn.type === 'SquadraToGirone') {
        baseLink.fromPosition = 1; // Sempre 1 per SquadraToGirone
      } else if (conn.type === 'GironeToGirone' || conn.type === 'GironeToDiretta' || 
                 conn.type === 'GironeToClassifica' || conn.type === 'DiretteToClassifica' || 
                 conn.type === 'SquadraToClassifica' || conn.type === 'ClassificaToGirone' ||
                 conn.type === 'ClassificaToDiretta' || conn.type === 'ClassificaToClassifica') {
        baseLink.fromPosition = conn.connectionNumber || 1; // Numero editabile
      } else {
        baseLink.fromPosition = conn.fromPosition || 1;
      }

      // Usa toPosition direttamente dalla connessione se definito,
      // altrimenti calcola come posizione nella lista ordinata delle connessioni in ingresso
      if (conn.toPosition !== null && conn.toPosition !== undefined) {
        // Usa il toPosition già salvato nella connessione (impostato al click sulla porta)
        baseLink.toPosition = conn.toPosition;
      } else if (targetNode) {
        // Fallback: Ottieni tutte le connessioni in ingresso del nodo target
        const incomingConnections = connections.filter(c => c.toId === targetNode.id);

        // Ordina le connessioni basandosi su incomingConnectionsOrder
        const orderMap = targetNode.incomingConnectionsOrder || {};
        const sortedConnections = incomingConnections
          .map(c => ({ ...c, order: orderMap[c.id] || 0 }))
          .sort((a, b) => a.order - b.order);

        // Trova la posizione della connessione corrente nella lista ordinata (1-based)
        const position = sortedConnections.findIndex(c => c.id === conn.id);
        baseLink.toPosition = position >= 0 ? position + 1 : 1;
      } else {
        baseLink.toPosition = 1;
      }

      return baseLink;
    });

    const result = { fasi, links };
    if (formulaSortPriority && formulaSortPriority.length > 0) {
      result.sortPriority = formulaSortPriority;
    }
    if (gruppiFase.length > 0) {
      // Scarta i riferimenti a nodi cancellati nel frattempo, e i gruppi che restano vuoti.
      const idEsistenti = new Set(nodes.map(n => n.originalId ?? parseInt(n.id.replace('node-', ''), 10)));
      const puliti = gruppiFase
        .map(g => ({ ...g, nodi: g.nodi.filter(id => idEsistenti.has(id)) }))
        .filter(g => g.nodi.length > 0);
      if (puliti.length > 0) {
        result.gruppiFase = puliti;
      }
    }
    return result;
  }, [nodes, connections, formulaSortPriority, gruppiFase]);

  // Funzione per calcolare il bounding box di tutti gli elementi
  const calculateBoundingBox = useCallback(() => {
    if (nodes.length === 0) {
      return { minX: 0, minY: 0, maxX: 800, maxY: 600 };
    }

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    nodes.forEach(node => {
      minX = Math.min(minX, node.x);
      minY = Math.min(minY, node.y);
      maxX = Math.max(maxX, node.x + node.width);
      maxY = Math.max(maxY, node.y + node.height);
    });

    return { minX, minY, maxX, maxY };
  }, [nodes]);

  // Funzione per adattare la vista a tutti gli elementi
  const fitToView = useCallback(() => {
    if (!canvasRef.current || nodes.length === 0) return;

    const boundingBox = calculateBoundingBox();
    const canvasRect = canvasRef.current.getBoundingClientRect();
    
    const contentWidth = boundingBox.maxX - boundingBox.minX;
    const contentHeight = boundingBox.maxY - boundingBox.minY;
    
    // Aggiungi padding intorno al contenuto
    const padding = 50;
    const paddedWidth = contentWidth + (padding * 2);
    const paddedHeight = contentHeight + (padding * 2);
    
    // Calcola il fattore di scala per adattare il contenuto alla vista
    const scaleX = canvasRect.width / paddedWidth;
    const scaleY = canvasRect.height / paddedHeight;
    const scale = Math.min(scaleX, scaleY, 1); // Non zoomare oltre 100%
    
    // Calcola la traslazione per centrare il contenuto
    const centerX = (boundingBox.minX + boundingBox.maxX) / 2;
    const centerY = (boundingBox.minY + boundingBox.maxY) / 2;
    
    const translateX = canvasRect.width / 2 - centerX * scale;
    const translateY = canvasRect.height / 2 - centerY * scale;
    
    setTransform({
      scale: Math.max(0.1, scale),
      translateX,
      translateY
    });
  }, [calculateBoundingBox]); // Rimuovo nodes dalle dipendenze

  const exportToJSON = useCallback(() => {
    const data = convertToNewFormat();
    console.log(data)
    const jsonString = JSON.stringify(data, null, 2);
    
    // Recupera l'ID del torneo dal localStorage
    const storedEdition = localStorage.getItem('selectedEdition');
    if (!storedEdition) {
      alert('Errore: nessun torneo selezionato');
      return;
    }
    
    const edizione = JSON.parse(storedEdition);
    const torneoId = edizione.id;
    
    API.torneo.setFormula(torneoId, jsonString).then(r => {
      if(r.isSuccess){
        console.log("Formula salvata con successo");
      } else {
        console.log("Salvataggio fallito:", r.errorMsg);
      }
    }).catch(error => {
      console.error("Errore nella chiamata API:", error);
    });

  }, [convertToNewFormat]);
  
  const loadFormulaFromAPI = useCallback(async (idTorneo = 17) => {
    try {
      const response = await API.torneo.getFormula(idTorneo);
      if (response.isSuccess && response.data) {
        
        // Il backend restituisce una stringa JSON che deve essere deserializzata
        const parsedData = JSON.parse(response.data);
        const { nodes: importedNodes, connections: importedConnections } = convertFromNewFormat(parsedData);
        setNodes(importedNodes);
        setConnections(importedConnections);
        setSelectedNodes([]);
        setFormulaSortPriority(Array.isArray(parsedData.sortPriority) && parsedData.sortPriority.length > 0 ? parsedData.sortPriority : null);
        setGruppiFase(Array.isArray(parsedData.gruppiFase) ? parsedData.gruppiFase : []);
        console.log('Formula caricata con successo dall\'API');
      } else {
        console.log('Nessuna formula trovata per il torneo', idTorneo);
      }
    } catch (error) {
      console.error('Errore nel caricamento della formula:', error);
    }
  }, [convertFromNewFormat]);

  const importFromJSON = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (event) => {
          try {
            const data = JSON.parse(event.target.result);
            const { nodes: importedNodes, connections: importedConnections } = convertFromNewFormat(data);
            setNodes(importedNodes);
            setConnections(importedConnections);
            setSelectedNodes([]);
            setFormulaSortPriority(Array.isArray(data.sortPriority) && data.sortPriority.length > 0 ? data.sortPriority : null);
            setGruppiFase(Array.isArray(data.gruppiFase) ? data.gruppiFase : []);
            console.log('Dati importati con successo');
          } catch (error) {
            console.error('Errore nell\'importazione:', error);
            alert('Errore nel parsing del file JSON');
          }
        };
        reader.readAsText(file);
      }
    };
    input.click();
  }, [convertFromNewFormat]);


  // Funzione per aggiornare i numeri delle connessioni
  const updateConnectionNumber = useCallback((connectionId, newNumber) => {
    setConnections(prev => prev.map(conn => 
      conn.id === connectionId 
        ? { ...conn, connectionNumber: newNumber }
        : conn
    ));
  }, []);

  const getMouseCoordinates = useCallback((e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const t = transformRef.current;
    return {
      x: (e.clientX - rect.left - t.translateX) / t.scale,
      y: (e.clientY - rect.top - t.translateY) / t.scale
    };
  }, []);

  // Un solo aggiornamento per frame. Un mouse da gaming manda anche 500 eventi al secondo: senza
  // questo, ognuno faceva un setState e un ridisegno completo del canvas.
  //
  // Il frame gia' in coda non si annulla, si aggiorna solo il lavoro che dovra' fare. Non cambia
  // quando l'aggiornamento arriva a schermo - cancelAnimationFrame seguito da una nuova
  // requestAnimationFrame si riaggancia comunque allo stesso frame - ma con 500 eventi al secondo
  // risparmia 500 coppie cancel/request per frame, ridotte a una sola scrittura di ref.
  const framePendente = useRef(null);
  const azionePendente = useRef(null);
  const perFrame = useCallback((azione) => {
    azionePendente.current = azione;
    if (framePendente.current !== null) return;
    framePendente.current = requestAnimationFrame(() => {
      framePendente.current = null;
      const daFare = azionePendente.current;
      azionePendente.current = null;
      daFare?.();
    });
  }, []);

  useEffect(() => () => {
    if (framePendente.current !== null) cancelAnimationFrame(framePendente.current);
  }, []);

  const handleNodeSelect = useCallback((node, e) => {
    if (linkingMode) {
      e.stopPropagation();
      createMultipleLinks(node.id);
      return;
    }
    
    const isCtrlClick = e?.ctrlKey || e?.metaKey;

    if (isCtrlClick) {
      setSelectedNodes(prev => prev.some(n => n.id === node.id)
        ? prev.filter(n => n.id !== node.id)
        : [...prev, node]);
      return;
    }

    // Cliccando un nodo che fa gia' parte della selezione, la selezione resta: prima veniva
    // ridotta a quel solo nodo mentre il trascinamento muoveva tutto il gruppo, e a fine gesto
    // ti ritrovavi venti nodi spostati e uno solo selezionato.
    const nelGruppo = selectedNodes.some(n => n.id === node.id);
    const selezione = nelGruppo ? selectedNodes : [node];
    if (!nelGruppo) setSelectedNodes(selezione);

    startDrag(node.id, getMouseCoordinates(e), selezione);
  }, [linkingMode, selectedNodes, startDrag, getMouseCoordinates]);

  // Pan e selezione rettangolare vivono su document, non sull'<svg>: rilasciando il tasto fuori
  // dal riquadro il mouseup non arrivava mai, e il canvas restava incollato al puntatore fino al
  // click successivo.
  const handleCanvasMouseDown = useCallback((e) => {
    // Tasto centrale: pan da qualunque punto, anche sopra un nodo. Sinistro: solo dallo sfondo.
    const tastoCentrale = e.button === 1;
    const sulloSfondo = e.target === canvasRef.current;
    if (!tastoCentrale && (e.button !== 0 || !sulloSfondo)) return;

    e.preventDefault();

    if (!tastoCentrale && (e.ctrlKey || e.metaKey)) {
      // Selezione rettangolare
      const inizio = getMouseCoordinates(e);
      setSelectionState({
        isSelecting: true,
        startPoint: inizio,
        rect: { x: inizio.x, y: inizio.y, width: 0, height: 0 }
      });

      const muovi = (ev) => perFrame(() => setSelectionState(prev => ({
        ...prev,
        rect: rettangoloFra(inizio, getMouseCoordinates(ev))
      })));

      const rilascia = (ev) => {
        document.removeEventListener('mousemove', muovi);
        document.removeEventListener('mouseup', rilascia);
        const rect = rettangoloFra(inizio, getMouseCoordinates(ev));
        setSelectionState({ isSelecting: false, startPoint: null, rect: null });

        if (rect.width > 5 && rect.height > 5) {
          setSelectedNodes(nodiNelRettangolo(nodesRef.current, rect));
        }
      };

      document.addEventListener('mousemove', muovi);
      document.addEventListener('mouseup', rilascia);
      return;
    }

    // Pan
    const origine = {
      x: e.clientX - transformRef.current.translateX,
      y: e.clientY - transformRef.current.translateY
    };
    if (canvasRef.current) canvasRef.current.style.cursor = 'grabbing';
    modoGesto(true);

    const partenza = { x: e.clientX, y: e.clientY };
    gestoInCorso.current = true;
    azzeraSpia();

    const muovi = (ev) => {
      if (Math.abs(ev.clientX - partenza.x) > 3 || Math.abs(ev.clientY - partenza.y) > 3) {
        appenaPannato.current = true;
      }
      // Niente setState qui dentro: si aggiorna la verita' in ref e la si scrive sul DOM.
      segnaEvento(ev);
      transformRef.current = {
        ...transformRef.current,
        translateX: ev.clientX - origine.x,
        translateY: ev.clientY - origine.y
      };
      // Scrittura diretta, senza passare da requestAnimationFrame.
      //
      // Chrome consegna gia' un solo mousemove coalescato per frame - si vede dal fatto che il
      // numero di eventi e quello di frame coincidono sempre - quindi il throttle non accorpava
      // niente: aggiungeva soltanto fino a un frame di ritardo fra l'evento e il pixel. Scrivere
      // un attributo e' idempotente e costa niente, quindi la strada piu' corta e' la migliore.
      scriviTransformSulDom(transformRef.current);
      segnaFrame();
    };

    const rilascia = () => {
      document.removeEventListener('mousemove', muovi);
      document.removeEventListener('mouseup', rilascia);
      gestoInCorso.current = false;
      modoGesto(false);
      if (canvasRef.current) canvasRef.current.style.cursor = 'grab';
      // Nessun setTransform: la posizione della vista vive in transformRef, che e' anche quello
      // che leggono lo zoom e fitToView. Riallineare lo stato costerebbe un commit e un ridisegno
      // completo dell'SVG per non cambiare un pixel di quello che si vede.
    };

    document.addEventListener('mousemove', muovi);
    document.addEventListener('mouseup', rilascia);
  }, [getMouseCoordinates, perFrame, modoGesto, segnaEvento, segnaFrame, azzeraSpia, scriviTransformSulDom]);

  const handleCanvasClick = useCallback((e) => {
    if (appenaPannato.current) {
      appenaPannato.current = false;
      return;
    }
    if (e.target === canvasRef.current) {
      if (!e.ctrlKey && !e.metaKey) {
        setSelectedNodes([]);
      }
      setSelectedConnection(null);
      setConnecting(null);
    }
  }, []);

  const createMultipleLinks = useCallback((targetNodeId) => {
    if (!selectedNodes.length || !targetNodeId) return;
    
    const newConnections = [];
    const maxConnId = Math.max(...connections.map(c => c.originalId || 0), 0);
    
    console.log(selectedNodes);
    selectedNodes.forEach((fromNode, index) => {
      // Evita di creare link verso se stesso
      if (fromNode.id === targetNodeId) return;
      
      // Verifica se esiste già una connessione
      // const existingConnection = connections.find(c => 
      //   c.fromId === fromNode.id && c.toId === targetNodeId
      // );
      // if (existingConnection) return;
      
      const targetNode = nodes.find(n => n.id === targetNodeId);
      
      // Determina il tipo di connessione
      let connectionType = 'default';
      if (fromNode.type === 'team' && targetNode?.phaseType === 'Girone') {
        connectionType = 'SquadraToGirone';
      } else if (fromNode.type === 'team' && targetNode?.phaseType === 'Dirette') {
        connectionType = 'SquadraToDiretta';
      } else if (fromNode.type === 'team' && targetNode?.phaseType === 'Classifica') {
        connectionType = 'SquadraToClassifica';
      } else if (fromNode.phaseType === 'Girone' && targetNode?.phaseType === 'Dirette') {
        connectionType = 'GironeToDiretta';
      } else if (fromNode.phaseType === 'Girone' && targetNode?.phaseType === 'Girone') {
        connectionType = 'GironeToGirone';
      } else if (fromNode.phaseType === 'Girone' && targetNode?.phaseType === 'Classifica') {
        connectionType = 'GironeToClassifica';
      } else if (fromNode.phaseType === 'Dirette' && targetNode?.phaseType === 'Classifica') {
        connectionType = 'DiretteToClassifica';
      } else if (fromNode.phaseType === 'Classifica' && targetNode?.phaseType === 'Girone') {
        connectionType = 'ClassificaToGirone';
      } else if (fromNode.phaseType === 'Classifica' && targetNode?.phaseType === 'Dirette') {
        connectionType = 'ClassificaToDiretta';
      } else if (fromNode.phaseType === 'Classifica' && targetNode?.phaseType === 'Classifica') {
        connectionType = 'ClassificaToClassifica';
      }

      const newConnection = {
        id: `conn-${maxConnId + 1 + index}`,
        originalId: maxConnId + 1 + index,
        fromId: fromNode.id,
        toId: targetNodeId,
        type: connectionType
      };
      
      newConnections.push(newConnection);
    });
    
    if (newConnections.length > 0) {
      setConnections(prev => [...prev, ...newConnections]);

      // Aggiorna l'ordine delle connessioni in ingresso del nodo target
      const targetNode = nodes.find(n => n.id === targetNodeId);
      if (targetNode) {
        const currentIncomingConnections = connections.filter(c => c.toId === targetNodeId);
        const totalIncomingCount = currentIncomingConnections.length + newConnections.length;

        const updatedOrderMap = { ...(targetNode.incomingConnectionsOrder || {}) };
        newConnections.forEach((conn, index) => {
          updatedOrderMap[conn.id] = currentIncomingConnections.length + index;
        });

        // Se il target è Girone/Classifica, aggiorna anche l'altezza per le porte di output
        if (targetNode.phaseType === 'Girone' || targetNode.phaseType === 'Classifica') {
          const minHeightForPorts = Math.max(80, totalIncomingCount * 20 + 30);
          updateNode(targetNodeId, {
            incomingConnectionsOrder: updatedOrderMap,
            height: Math.max(targetNode.height, minHeightForPorts)
          });
        } else {
          updateNode(targetNodeId, { incomingConnectionsOrder: updatedOrderMap });
        }
      }
    }

    setLinkingMode(false);
  }, [selectedNodes, connections, nodes, updateNode]);

  // Caricamento automatico formula dal server al mount del componente
  useEffect(() => {
    const storedEdition = localStorage.getItem('selectedEdition');
    if (storedEdition) {
      const edizione = JSON.parse(storedEdition);
      loadFormulaFromAPI(edizione.id);
    } else {}
    
    // Invalida la cache delle squadre quando cambia l'edizione
    setCachedTeams(null);
    
  }, [loadFormulaFromAPI]);

  // Esegue fitToView una sola volta dopo il caricamento dei nodi
  useEffect(() => {
    if (nodes.length > 0 && canvasRef.current) {
      // Usa un timeout per assicurarsi che il canvas sia completamente renderizzato
      const timer = setTimeout(() => {
        fitToView();
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [nodes.length > 0 ? 'loaded' : 'empty']); // Dipende solo dal fatto che ci siano nodi o meno

  // Gestione evento wheel con passive: false per permettere preventDefault
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Come il pan: durante la raffica di eventi si scrive sul DOM, e lo stato si riallinea una
    // volta sola quando la rotella si ferma.
    let commit = null;
    const wheelHandler = (e) => {
      e.preventDefault();
      const { fattore } = interpretaRotella(e);
      if (fattore === 1) return;

      const rect = canvas.getBoundingClientRect();
      transformRef.current = zoomAncorato(transformRef.current, fattore,
                                          e.clientX - rect.left, e.clientY - rect.top);
      gestoInCorso.current = true;
      modoGesto(true);
      scriviTransformSulDom(transformRef.current);

      clearTimeout(commit);
      commit = setTimeout(() => {
        gestoInCorso.current = false;
        modoGesto(false);
        setTransform(transformRef.current);
      }, 120);
    };

    canvas.addEventListener('wheel', wheelHandler, { passive: false });
    return () => {
      clearTimeout(commit);
      canvas.removeEventListener('wheel', wheelHandler);
    };
  }, [scriviTransformSulDom, modoGesto]);

  // Trascinamento dei nodi: i listener si montano una volta all'inizio del gesto e si smontano
  // alla fine. Prima le dipendenze cambiavano identita' a ogni render, quindi durante il
  // trascinamento venivano rimossi e riaggiunti a ogni singolo movimento del mouse.
  useEffect(() => {
    if (!isDragging) return;
    azzeraSpia();
    gestoInCorso.current = true;

    const muovi = (e) => {
      segnaEvento(e);
      perFrame(() => {
        updateDrag(getMouseCoordinates(e));
        segnaFrame();
      });
    };
    const rilascia = () => endDrag();

    document.addEventListener('mousemove', muovi);
    document.addEventListener('mouseup', rilascia);
    return () => {
      gestoInCorso.current = false;
      document.removeEventListener('mousemove', muovi);
      document.removeEventListener('mouseup', rilascia);
    };
  }, [isDragging, updateDrag, endDrag, getMouseCoordinates, perFrame, azzeraSpia, segnaEvento, segnaFrame]);

  // Gestione hover sui nodi
  // Trascinando lo sfondo il puntatore attraversa i nodi, e ogni attraversamento cambiava
  // hoveredNode: quello stato arriva a tutte le connessioni, quindi un nodo sfiorato ne
  // ridisegnava 207. Durante un gesto l'evidenziazione non serve e si tace.
  const handleNodeHover = useCallback((nodeId) => {
    if (gestoInCorso.current) return;
    setHoveredNode(nodeId);
  }, []);

  const handleNodeLeave = useCallback(() => {
    if (gestoInCorso.current) return;
    setHoveredNode(null);
  }, []);

  // Gestione doppio click
  const handleNodeDoubleClick = useCallback((node) => {
    console.log('handleNodeDoubleClick called:', node);
    setSelectedNodes([node]);

    // Per nodi Dirette, l'editing è interno al componente (toolbar)
    // Per gli altri nodi, apri il property editor
    if (node.phaseType !== 'Dirette') {
      console.log('Opening property editor');
      setPropertyEditorOpen(true);
    }
  }, []);

  // Gestione doppio click su connessioni
  const handleConnectionDoubleClick = useCallback((connection) => {
    setSelectedConnection(connection);
    console.log('Doppio click su connessione:', connection);
  }, []);

  // Gestione connessioni - ora include anche la posizione della porta di output
  const startConnection = useCallback((nodeId, outputPosition = null) => {
    setConnecting({ nodeId, outputPosition });
  }, []);
  
  // endConnection ora accetta anche inputPosition (indice porta di input cliccata)
  const endConnection = useCallback((nodeId, inputPosition = null) => {
    // Dalle ref e non dalla closure: questa callback arriva a tutti i nodi memoizzati, e se
    // cambiasse identita' a ogni setNodes li farebbe ridisegnare tutti a ogni frame.
    const nodes = nodesRef.current;
    const connections = connectionsRef.current;
    if (connecting && connecting.nodeId && connecting.nodeId !== nodeId) {
      const fromNode = nodes.find(n => n.id === connecting.nodeId);
      const toNode = nodes.find(n => n.id === nodeId);

      // Determina il tipo di connessione basato sui nodi collegati
      let connectionType = 'default';
      if (fromNode.type === 'team' && toNode.phaseType === 'Girone') {
        connectionType = 'SquadraToGirone';
      } else if (fromNode.type === 'team' && toNode.phaseType === 'Dirette') {
        connectionType = 'SquadraToDiretta';
      } else if (fromNode.type === 'team' && toNode.phaseType === 'Classifica') {
        connectionType = 'SquadraToClassifica';
      } else if (fromNode.phaseType === 'Girone' && toNode.phaseType === 'Dirette') {
        connectionType = 'GironeToDiretta';
      } else if (fromNode.phaseType === 'Girone' && toNode.phaseType === 'Girone') {
        connectionType = 'GironeToGirone';
      } else if (fromNode.phaseType === 'Girone' && toNode.phaseType === 'Classifica') {
        connectionType = 'GironeToClassifica';
      } else if (fromNode.phaseType === 'Dirette' && toNode.phaseType === 'Classifica') {
        connectionType = 'DiretteToClassifica';
      } else if (fromNode.phaseType === 'Classifica' && toNode.phaseType === 'Girone') {
        connectionType = 'ClassificaToGirone';
      } else if (fromNode.phaseType === 'Classifica' && toNode.phaseType === 'Dirette') {
        connectionType = 'ClassificaToDiretta';
      } else if (fromNode.phaseType === 'Classifica' && toNode.phaseType === 'Classifica') {
        connectionType = 'ClassificaToClassifica';
      }

      const maxConnId = Math.max(...connections.map(c => c.originalId || 0), 0);

      // Usa la posizione della porta di output se specificata (per Girone/Classifica)
      // Altrimenti usa 1 come default
      const outputPosition = connecting.outputPosition || 1;

      const newConnection = {
        id: `conn-${maxConnId + 1}`,
        originalId: maxConnId + 1,
        fromId: connecting.nodeId,
        toId: nodeId,
        type: connectionType,
        // Imposta automaticamente connectionNumber dalla porta di output cliccata
        connectionNumber: outputPosition,
        // Salva anche la posizione della porta di input cliccata (se specificata)
        // inputPosition è 0-based dall'array, convertiamo a 1-based per coerenza
        toPosition: inputPosition !== null ? inputPosition + 1 : null
      };
      setConnections(prev => [...prev, newConnection]);

      // Aggiorna l'ordine delle connessioni in ingresso del nodo target
      const targetNode = nodes.find(n => n.id === nodeId);
      if (targetNode) {
        const currentIncomingConnections = connections.filter(c => c.toId === nodeId);
        const newIncomingCount = currentIncomingConnections.length + 1; // +1 per la nuova connessione
        const updatedOrderMap = { ...(targetNode.incomingConnectionsOrder || {}) };
        updatedOrderMap[newConnection.id] = currentIncomingConnections.length;

        // Se il target è Girone/Classifica, aggiorna anche l'altezza per le porte di output
        if (targetNode.phaseType === 'Girone' || targetNode.phaseType === 'Classifica') {
          const minHeightForPorts = Math.max(80, newIncomingCount * 20 + 30);
          updateNode(nodeId, {
            incomingConnectionsOrder: updatedOrderMap,
            height: Math.max(targetNode.height, minHeightForPorts)
          });
        } else {
          updateNode(nodeId, { incomingConnectionsOrder: updatedOrderMap });
        }
      }
    }
    setConnecting(null);
  }, [connecting, updateNode]);

  // Aggiunta nodi
  const addNode = useCallback((type, subType = null) => {
    const maxId = Math.max(...nodes.map(n => n.originalId || 0), 100);
    const newNode = {
      id: `node-${maxId + 1}`,
      originalId: maxId + 1,
      type,
      name: type === 'team' ? `Team ${nodes.filter(n => n.type === 'team').length + 1}`
                            : `${subType} ${nodes.filter(n => n.phaseType === subType).length + 1}`,
      x: 300 + Math.random() * 200,
      y: 200 + Math.random() * 200,
      width: 150,
      height: type === 'team' ? 40 : 80
    };

    if (type === 'phase') {
      newNode.phaseType = subType;

      // Inizializza proprietà compound node per nodi Dirette
      if (subType === 'Dirette') {
        newNode.expanded = true;
        newNode.width = 300;
        newNode.height = 200;
        newNode.internalMatches = [];
        newNode.internalConnections = [];
        newNode.inputMapping = [];
        newNode.outputMapping = [];
      }
    }

    setNodes(prev => [...prev, newNode]);
  }, [nodes]);

  // Eliminazione nodi
  const deleteNodes = useCallback((nodeIds) => {
    // Come endConnection: dalle ref, altrimenti l'identita' cambia a ogni setNodes.
    const nodes = nodesRef.current;
    const connections = connectionsRef.current;

    // Trova le connessioni che verranno eliminate
    const connectionsToDelete = connections.filter(conn => 
      nodeIds.includes(conn.fromId) || nodeIds.includes(conn.toId)
    );
    
    // Aggiorna l'ordine delle connessioni per i nodi che perdono connessioni in ingresso
    const affectedTargetNodes = new Set();
    connectionsToDelete.forEach(conn => {
      if (!nodeIds.includes(conn.toId)) {
        affectedTargetNodes.add(conn.toId);
      }
    });
    
    // Riordina le connessioni rimanenti per ogni nodo affetto
    affectedTargetNodes.forEach(targetNodeId => {
      const targetNode = nodes.find(n => n.id === targetNodeId);
      if (targetNode) {
        const remainingIncomingConnections = connections.filter(c => 
          c.toId === targetNodeId && 
          !connectionsToDelete.some(deleted => deleted.id === c.id)
        );
        
        const newOrderMap = {};
        remainingIncomingConnections.forEach((conn, index) => {
          newOrderMap[conn.id] = index;
        });
        
        updateNode(targetNodeId, { incomingConnectionsOrder: newOrderMap });
      }
    });
    
    setNodes(prev => prev.filter(node => !nodeIds.includes(node.id)));
    setConnections(prev => prev.filter(conn => 
      !nodeIds.includes(conn.fromId) && !nodeIds.includes(conn.toId)
    ));
    setSelectedNodes([]);
  }, [updateNode]);

  // Eliminazione singola connessione
  const deleteConnection = useCallback((connectionId) => {
    const connectionToDelete = connections.find(c => c.id === connectionId);
    if (!connectionToDelete) return;

    // Aggiorna l'ordine delle connessioni per il nodo target
    const targetNode = nodes.find(n => n.id === connectionToDelete.toId);
    if (targetNode) {
      const remainingIncomingConnections = connections.filter(c => 
        c.toId === connectionToDelete.toId && c.id !== connectionId
      );
      
      const newOrderMap = {};
      remainingIncomingConnections.forEach((conn, index) => {
        newOrderMap[conn.id] = index;
      });
      
      updateNode(connectionToDelete.toId, { incomingConnectionsOrder: newOrderMap });
    }

    setConnections(prev => prev.filter(conn => conn.id !== connectionId));
  }, [connections, nodes, updateNode]);

  // Controlli zoom (i pulsanti della toolbar).
  //
  // Cambiare la sola scala tiene fermo il punto (0,0) del mondo, che a formula caricata sta quasi
  // sempre fuori schermo: premendo + il grafo scappava via. Qui il punto fermo e' il centro della
  // vista, come si aspetta chi guarda.
  const zoomAlCentro = useCallback((fattore) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    const nuovo = zoomAncorato(transformRef.current, fattore, (rect?.width ?? 0) / 2, (rect?.height ?? 0) / 2);
    transformRef.current = nuovo;
    setTransform(nuovo);
  }, []);

  const zoomIn = useCallback(() => zoomAlCentro(1.2), [zoomAlCentro]);
  const zoomOut = useCallback(() => zoomAlCentro(1 / 1.2), [zoomAlCentro]);

  // Torna al 100% senza perdere di vista quello che stavi guardando: prima riportava anche la
  // traslazione a zero, e con nodi a coordinate grandi il risultato era una vista vuota.
  const resetZoom = useCallback(() => {
    zoomAlCentro(1 / transformRef.current.scale);
  }, [zoomAlCentro]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Delete' && selectedNodes.length > 0) {
        deleteNodes(selectedNodes.map(n => n.id));
      } else if (e.key === 'Escape') {
        setSelectedNodes([]);
        setSelectedConnection(null);
        setConnecting(null);
        setLinkingMode(false); 
      } else if (e.ctrlKey && e.key === 'a') {
        e.preventDefault();
        setSelectedNodes([...nodes]);
      } else if (e.ctrlKey && e.key === 's') {
        e.preventDefault();
        exportToJSON();
      } else if (e.ctrlKey && e.key === 'o') {
        e.preventDefault();
        importFromJSON();
      } else if (e.altKey && e.key === 'c') {
        e.preventDefault();
        setLinkingMode(v => !v)
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedNodes, nodes, exportToJSON, importFromJSON]);

  const handleUpdateElements = useCallback((alignedElements) => {
    const updatedNodes = nodes.map(node => {
      const alignedElement = alignedElements.find(aligned => aligned.id === node.id);
      return alignedElement || node;
    });
    setNodes(updatedNodes);

    setSelectedNodes(prev => prev.map(selectedNode => {
      const alignedElement = alignedElements.find(aligned => aligned.id === selectedNode.id);
      return alignedElement || selectedNode;
    }));
  }, [nodes]);

  const creaPlanning = () => {
    setPlanningModalOpen(true);
  }

  // --- Gruppi di fase ---------------------------------------------------------------------
  // Un gruppo dice "questi nodi sono una fase sola", scavalcando il calcolo automatico che li
  // separerebbe. L'ordine fra le fasi resta quello del grafo: il backend collassa ogni gruppo
  // in un nodo unico e ricalcola.

  const COLORI_GRUPPI = ['#e74c3c', '#8e44ad', '#16a085', '#d35400', '#2980b9', '#c0392b'];

  const idNodo = (node) => node.originalId ?? parseInt(node.id.replace('node-', ''), 10);

  // Solo Gironi ed Eliminazioni dirette fanno una fase: Squadra e Classifica seguono.
  const raggruppabile = (node) => node.type === 'phase' && (node.phaseType === 'Girone' || node.phaseType === 'Dirette');

  const gruppoDelNodo = useCallback((node) => {
    const id = idNodo(node);
    const indice = gruppiFase.findIndex(g => g.nodi.includes(id));
    return indice < 0 ? null : { indice, gruppo: gruppiFase[indice] };
  }, [gruppiFase]);

  const decorazioneGruppo = useCallback((node) => {
    const trovato = gruppoDelNodo(node);
    if (!trovato) return null;
    return {
      colore: COLORI_GRUPPI[trovato.indice % COLORI_GRUPPI.length],
      etichetta: `F${trovato.indice + 1}`
    };
  }, [gruppoDelNodo]);

  // Indici calcolati una volta per render invece che dentro ogni nodo e ogni connessione.
  // Cercare gli estremi con nodes.find() e contare gli ingressi con connections.filter() dentro i
  // 303 componenti della formula del 98 costava circa 82.000 scansioni di array a ogni movimento
  // del mouse.
  const nodiPerId = useMemo(() => new Map(nodes.map(n => [n.id, n])), [nodes]);
  const ingressiPerNodo = useMemo(() => {
    const conta = new Map();
    connections.forEach(c => conta.set(c.toId, (conta.get(c.toId) || 0) + 1));
    return conta;
  }, [connections]);

  // Identita' stabile: passare una arrow inline farebbe cambiare la prop a ogni render e
  // annullerebbe la memoizzazione dei componenti.
  const handleNodeDelete = useCallback((nodeId) => deleteNodes([nodeId]), [deleteNodes]);
  const handleConnectionDelete = useCallback(
    (id) => setConnections(prev => prev.filter(c => c.id !== id)), []);

  const nodiSelezionatiRaggruppabili = selectedNodes.filter(raggruppabile);
  const selezioneGiaRaggruppata = nodiSelezionatiRaggruppabili.length > 0
    && nodiSelezionatiRaggruppabili.every(n => gruppoDelNodo(n) !== null);

  const raggruppaInFase = useCallback(() => {
    const ammessi = selectedNodes.filter(raggruppabile);
    const scartati = selectedNodes.length - ammessi.length;
    if (ammessi.length === 0) {
      alert('Una fase si compone di Gironi ed Eliminazioni dirette: nella selezione non ce ne sono.');
      return;
    }

    const nome = window.prompt(
      `Nome della fase per ${ammessi.length} nodi` +
      (scartati > 0 ? ` (${scartati} nodi non raggruppabili verranno ignorati)` : '') +
      '\n\nLascia vuoto per non darle un nome.',
      ''
    );
    if (nome === null) return; // annullato

    const ids = ammessi.map(idNodo);
    setGruppiFase(prev => {
      // Un nodo sta in un gruppo solo: lo si toglie da dove era.
      const ripuliti = prev
        .map(g => ({ ...g, nodi: g.nodi.filter(id => !ids.includes(id)) }))
        .filter(g => g.nodi.length > 0);
      const nuovoId = ripuliti.reduce((max, g) => Math.max(max, g.id), 0) + 1;
      return [...ripuliti, { id: nuovoId, nome: nome.trim() || null, nodi: ids }];
    });
  }, [selectedNodes]);

  const sciogliFase = useCallback(() => {
    const ids = selectedNodes.map(idNodo);
    setGruppiFase(prev => prev
      .map(g => ({ ...g, nodi: g.nodi.filter(id => !ids.includes(id)) }))
      .filter(g => g.nodi.length > 0));
  }, [selectedNodes]);

  // Gestione cache squadre
  const handleLoadTeams = useCallback((teams) => {
    setCachedTeams(teams);
  }, []);

  return (
    <div className="tournament-formula-container">
      <div className="main-toolbar">
        {/* a torneo chiuso la formula è in sola visualizzazione: niente aggiunta/salvataggio */}
        {!readOnly && (
          <>
            <div className="toolbar-section">
              <button onClick={() => addNode('team')}>+ Squadra</button>
              <button onClick={() => addNode('phase', 'Girone')}>+ Girone</button>
              <button onClick={() => addNode('phase', 'Dirette')}>+ Eliminazione</button>
              <button onClick={() => addNode('phase', 'Classifica')}>+ Classifica</button>
            </div>

            <div className="toolbar-separator" />

            <div className="toolbar-section">
              <button onClick={exportToJSON} title="Esporta dati in formato JSON">
                📤 Salva
              </button>
              <button onClick={importFromJSON} title="Importa dati da file JSON">
                📥 Importa JSON
              </button>
              <button onClick={creaPlanning} title="Importa dati da file JSON">
                📋 Crea Planning
              </button>
              <button onClick={() => setSortPriorityModalOpen(true)} title="Priorità ordinamento classifiche gironi">
                ⚙️ Priorità Classifiche
              </button>
            </div>

            <div className="toolbar-separator" />
          </>
        )}

        <div className="toolbar-section zoom-controls">
          <button onClick={zoomOut} title="Zoom Out">−</button>
          <span className="zoom-level">{Math.round(transform.scale * 100)}%</span>
          <button onClick={zoomIn} title="Zoom In">+</button>
          <button onClick={resetZoom} title="Reset Zoom">🔄</button>
          <button onClick={fitToView} title="Fit to View">📐</button>
        </div>

        {!readOnly && (
        <button
          className={`link-button ${linkingMode ? 'active' : ''}`}
          onClick={() => setLinkingMode(linkingMode => !linkingMode)}
          title={linkingMode ? 'Modalità Link attiva - Clicca su un nodo target' : 'Collega i nodi selezionati al prossimo nodo cliccato'}
        >
          🔗 {linkingMode ? 'Link Attivo' : 'Link'}
        </button>
        )}

        {!readOnly && selectedNodes.length > 0 && (
          <>
            <div className="toolbar-separator" />
            <div className="toolbar-section">
              <button className="delete-button">🗑️</button>
            </div>

            <div className="toolbar-separator" />

            <div className="toolbar-section">
              {selezioneGiaRaggruppata ? (
                <button
                  onClick={sciogliFase}
                  title="Togli i nodi selezionati dal loro gruppo-fase: torneranno al calcolo automatico"
                >
                  ✂️ Sciogli fase
                </button>
              ) : (
                <button
                  onClick={raggruppaInFase}
                  disabled={nodiSelezionatiRaggruppabili.length === 0}
                  title="Dichiara che i nodi selezionati sono una sola fase, anche se il calcolo automatico li separerebbe"
                >
                  🎯 Raggruppa in fase
                </button>
              )}
            </div>
            
            
            {selectedNodes.length >= 2 && (
              <>
                <div className="toolbar-separator" />
                <div className="toolbar-section alignment-section">
                  <AlignmentToolbar 
                    selectedElements={selectedNodes}
                    onUpdateElements={handleUpdateElements}
                  />
                </div>
              </>
            )}
          </>
        )}
      </div>

      <div className="canvas-container">
        {debug.current && (
          <div
            ref={spiaDomRef}
            style={{
              position: 'absolute', top: 8, left: 8, zIndex: 100,
              background: 'rgba(0,0,0,0.8)', color: '#0f0', padding: '6px 10px',
              font: '12px monospace', borderRadius: 4, pointerEvents: 'none'
            }}
          >
            muovi il canvas per misurare
          </div>
        )}
        {debug.current && (
          <div
            ref={spiaFrameRef}
            style={{
              position: 'absolute', top: 40, left: 8, zIndex: 100,
              background: 'rgba(0,0,0,0.85)', color: '#ff0', padding: '6px 10px',
              font: '12px monospace', borderRadius: 4, pointerEvents: 'none',
              whiteSpace: 'pre'
            }}
          />
        )}
        <svg
          ref={canvasRef}
          className="formula-canvas"
          onMouseDown={handleCanvasMouseDown}
          onClick={handleCanvasClick}
          onContextMenu={(e) => e.preventDefault()}
          width="100%"
          height="100%"
          style={{ cursor: selectionState.isSelecting ? 'crosshair' : 'grab' }}
        >
          {/* Le punte di freccia, definite una volta per tutto il canvas invece che una per
              connessione: erano 771 elementi SVG (257 defs + 257 marker + 257 polygon) per
              disegnare una manciata di triangoli tutti uguali a parte il colore. */}
          <defs>
            {COLORI_FRECCIA.map((colore) => (
              <marker
                key={colore}
                id={idFreccia(colore)}
                markerWidth="10"
                markerHeight="7"
                refX="9"
                refY="3.5"
                orient="auto"
              >
                <polygon points="0 0, 10 3.5, 0 7" fill={colore} />
              </marker>
            ))}
          </defs>

          {/* Nessun transform qui: l'attributo lo scrive solo scriviTransformSulDom, nel layout
              effect qui sotto e durante i gesti. Con React come secondo scrittore, ogni suo
              ridisegno rimetteva il valore fermo all'inizio del gesto e la vista rimbalzava. */}
          <g ref={contenutoRef} style={{ transformOrigin: '0 0', transformBox: 'view-box' }}>
            {/* Connessioni */}
            {connections.map(connection => (
              <Connection
                key={connection.id}
                connection={connection}
                fromNode={nodiPerId.get(connection.fromId)}
                toNode={nodiPerId.get(connection.toId)}
                incomingToFrom={ingressiPerNodo.get(connection.fromId) || 0}
                onDelete={handleConnectionDelete}
                onDoubleClick={handleConnectionDoubleClick}
                selected={selectedConnection?.id === connection.id}
                hoveredNode={hoveredNode}
              />
            ))}

            {/* Nodi */}
            {nodes.map(node => {
              // Calcola il numero di connessioni in ingresso per questo nodo
              const incomingConnectionsCount = ingressiPerNodo.get(node.id) || 0;
              const gruppo = decorazioneGruppo(node);

              return node.phaseType === 'Dirette' ? (
                // CompoundNode non conosce i gruppi: la cornice la disegniamo qui accanto.
                <g key={node.id}>
                  {gruppo && (
                    <>
                      <rect
                        x={node.x - 4}
                        y={node.y - 4}
                        width={(node.width || 300) + 8}
                        height={(node.height || 200) + 8}
                        fill="none"
                        stroke={gruppo.colore}
                        strokeWidth="2"
                        strokeDasharray="5 3"
                        rx="11"
                        pointerEvents="none"
                      />
                      <rect
                        x={node.x + (node.width || 300) - 18}
                        y={node.y - 11}
                        width="22"
                        height="14"
                        fill={gruppo.colore}
                        rx="3"
                        pointerEvents="none"
                      />
                      <text
                        x={node.x + (node.width || 300) - 7}
                        y={node.y - 1}
                        textAnchor="middle"
                        fill="white"
                        fontSize="9"
                        fontWeight="bold"
                        pointerEvents="none"
                      >
                        {gruppo.etichetta}
                      </text>
                    </>
                  )}
                <CompoundNode
                  node={node}
                  selected={selectedNodes.some(n => n.id === node.id)}
                  onSelect={handleNodeSelect}
                  onDoubleClick={handleNodeDoubleClick}
                  onDelete={handleNodeDelete}
                  onStartConnection={startConnection}
                  onEndConnection={endConnection}
                  onHover={handleNodeHover}
                  onLeave={handleNodeLeave}
                  onUpdateNode={updateNode}
                  connecting={connecting?.nodeId}
                  scale={transform.scale}
                />
                </g>
              ) : (
                <Node
                  key={node.id}
                  node={node}
                  gruppoFase={gruppo}
                  selected={selectedNodes.some(n => n.id === node.id)}
                  onSelect={handleNodeSelect}
                  onDoubleClick={handleNodeDoubleClick}
                  onDelete={handleNodeDelete}
                  onStartConnection={startConnection}
                  onEndConnection={endConnection}
                  onHover={handleNodeHover}
                  onLeave={handleNodeLeave}
                  connecting={connecting?.nodeId}
                  incomingConnectionsCount={incomingConnectionsCount}
                />
              );
            })}

            {/* Rettangolo di selezione */}
            {selectionState.rect && (
              <rect
                x={selectionState.rect.x}
                y={selectionState.rect.y}
                width={selectionState.rect.width}
                height={selectionState.rect.height}
                fill="rgba(33, 150, 243, 0.2)"
                stroke="#2196F3"
                strokeWidth={1 / transform.scale}
                strokeDasharray={`${5 / transform.scale},${5 / transform.scale}`}
                pointerEvents="none"
              />
            )}
          </g>
        </svg>
        
        {/* Istruzioni d'uso */}
        <div className="canvas-instructions">
          🖱️ Trascina per pan • Ctrl+Trascina per selezione • Rotella per zoom
        </div>
      </div>

      {propertyEditorOpen && selectedNodes.length === 1 && (
        <PropertyEditor
          node={selectedNodes[0]}
          connections={connections}
          nodes={nodes}
          onUpdate={updateNode}
          onUpdateConnectionNumber={updateConnectionNumber}
          onDeleteConnection={deleteConnection}
          onClose={() => setPropertyEditorOpen(false)}
          cachedTeams={cachedTeams}
          onLoadTeams={handleLoadTeams}
        />
      )}

      {sortPriorityModalOpen && (
        <SortPriorityModal
          initial={formulaSortPriority}
          onClose={() => setSortPriorityModalOpen(false)}
          onSave={(newPriority) => {
            setFormulaSortPriority(newPriority);
            setSortPriorityModalOpen(false);
          }}
        />
      )}

      {/* Modal per configurazione planning */}
      {planningModalOpen && (
        <PlanningModal 
          onClose={() => setPlanningModalOpen(false)}
          onConfirm={async (config) => {
            try {
              console.log('Configurazione planning:', config);
              const edizione = JSON.parse(localStorage.getItem('selectedEdition') || '{}');
              const response = await API.planning.create(edizione.id);
              
              if (response.isSuccess) {
                console.log('Planning creato con successo');
                alert('Planning creato con successo!');
              } else {
                console.error('Errore nella creazione del planning:', response.errorMsg);
                alert('Errore nella creazione del planning: ' + (response.errorMsg || 'Errore sconosciuto'));
              }
            } catch (error) {
              console.error('Errore nella chiamata API:', error);
              alert('Errore di rete nella creazione del planning');
            } finally {
              setPlanningModalOpen(false);
            }
          }}
        />
      )}
    </div>
  );
};

// Componente Modal per configurazione Planning
const PlanningModal = ({ onClose, onConfirm }) => {
  const [campiMax, setCampiMax] = useState('');
  const [arbitraggioSquadre, setArbitraggioSquadre] = useState(false);
  const [gironeRiposo, setGironeRiposo] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const numCampi = parseInt(campiMax);
    
    if (!campiMax || isNaN(numCampi) || numCampi <= 0) {
      alert('Inserisci un numero valido di campi (maggiore di 0)');
      return;
    }
    
    if (numCampi > 50) {
      alert('Il numero massimo di campi non può superare 50');
      return;
    }
    
    onConfirm({
      campiMax: numCampi,
      arbitraggioSquadre,
      gironeRiposo
    });
  };

  return (
    <div className="planning-modal-overlay">
      <div className="planning-modal">
        <div className="planning-modal-header">
          <h3>Configurazione Planning</h3>
          <button 
            className="planning-modal-close" 
            onClick={onClose}
            type="button"
          >
            ✕
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="planning-modal-content">
          <div className="planning-form-group">
            <label htmlFor="campiMax">Numero massimo di campi disponibili:</label>
            <input
              type="number"
              id="campiMax"
              value={campiMax}
              onChange={(e) => setCampiMax(e.target.value)}
              min="1"
              max="50"
              placeholder="es. 8"
              required
            />
          </div>

          <div className="planning-form-group">
            <label className="planning-checkbox-container">
              <input
                type="checkbox"
                checked={arbitraggioSquadre}
                onChange={(e) => setArbitraggioSquadre(e.target.checked)}
              />
              <span className="planning-checkmark"></span>
              Arbitraggio squadre
            </label>
          </div>

          <div className="planning-form-group">
            <label className="planning-checkbox-container">
              <input
                type="checkbox"
                checked={gironeRiposo}
                onChange={(e) => setGironeRiposo(e.target.checked)}
              />
              <span className="planning-checkmark"></span>
              Girone di riposo
            </label>
          </div>

          <div className="planning-modal-actions">
            <button 
              type="button" 
              className="planning-btn planning-btn-cancel"
              onClick={onClose}
            >
              Annulla
            </button>
            <button 
              type="submit" 
              className="planning-btn planning-btn-confirm"
            >
              Crea
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const DEFAULT_SORT_PRIORITY = ['punti', 'diff mete', 'piu pieni', 'meno falli', 'meno vuoti'];
const SORT_PRIORITY_LABELS = {
  'punti': 'Punti',
  'diff mete': 'Differenza mete',
  'piu pieni': 'Più pieni',
  'meno falli': 'Meno falli',
  'meno vuoti': 'Meno vuoti'
};

const normalizeSortPriority = (list) => {
  const src = Array.isArray(list) && list.length > 0 ? list : DEFAULT_SORT_PRIORITY;
  const valid = src.filter(k => DEFAULT_SORT_PRIORITY.includes(k));
  const missing = DEFAULT_SORT_PRIORITY.filter(k => !valid.includes(k));
  return [...valid, ...missing];
};

const SortPriorityModal = ({ initial, onClose, onSave }) => {
  const [priority, setPriority] = useState(normalizeSortPriority(initial));

  const move = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= priority.length) return;
    const next = [...priority];
    [next[index], next[target]] = [next[target], next[index]];
    setPriority(next);
  };

  const reset = () => setPriority([...DEFAULT_SORT_PRIORITY]);

  const handleSave = () => {
    const isDefault = priority.every((k, i) => k === DEFAULT_SORT_PRIORITY[i]);
    onSave(isDefault ? null : priority);
  };

  return (
    <div className="planning-modal-overlay" onClick={onClose}>
      <div className="planning-modal" onClick={(e) => e.stopPropagation()}>
        <div className="planning-modal-header">
          <h3>Priorità ordinamento classifiche gironi</h3>
          <button className="planning-modal-close" onClick={onClose} type="button">✕</button>
        </div>
        <div className="planning-modal-content">
          <p style={{ fontSize: 13, color: '#666', marginTop: 0 }}>
            Questa priorità si applica a tutte le classifiche dei gironi del torneo.
            I singoli nodi Classifica (violetti) possono sovrascriverla.
          </p>
          <ol className="sort-priority-list">
            {priority.map((key, index) => (
              <li key={key} className="sort-priority-item">
                <span className="sort-priority-rank">{index + 1}.</span>
                <span className="sort-priority-label">{SORT_PRIORITY_LABELS[key] || key}</span>
                <div className="sort-priority-actions">
                  <button
                    type="button"
                    className="sort-priority-btn"
                    onClick={() => move(index, -1)}
                    disabled={index === 0}
                    title="Sposta su"
                  >▲</button>
                  <button
                    type="button"
                    className="sort-priority-btn"
                    onClick={() => move(index, 1)}
                    disabled={index === priority.length - 1}
                    title="Sposta giù"
                  >▼</button>
                </div>
              </li>
            ))}
          </ol>
          <button type="button" className="sort-priority-reset" onClick={reset}>
            Ripristina predefinito
          </button>
          <div className="planning-modal-actions">
            <button type="button" className="planning-btn planning-btn-cancel" onClick={onClose}>
              Annulla
            </button>
            <button type="button" className="planning-btn planning-btn-confirm" onClick={handleSave}>
              Salva
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TournamentFormulaCanvas;